/**
 * Admin Alpha Dashboard
 *
 * Admin interface for reviewing and approving/rejecting alpha access requests.
 * Requires admin key authentication.
 */

import { useState, useEffect, useCallback } from 'react';
import styles from './AdminBetaPage.module.css';
import { getApiUrl } from '../config/api';

const API_BASE_URL = getApiUrl();

interface BetaRequest {
  id: string;
  email: string;
  source: string;
  motivation: string;
  discordHandle?: string;
  requestedAt: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewedAt?: string;
}

interface RequestsResponse {
  requests: BetaRequest[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';

export function AdminBetaPage() {
  // Admin key state
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem('adminKey') || '');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState('');

  // Data state
  const [requests, setRequests] = useState<BetaRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Pagination and filtering
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Action state
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  /**
   * Fetch beta requests from API
   */
  const fetchRequests = useCallback(async () => {
    if (!adminKey) return;

    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: '20',
      });

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await fetch(`${API_BASE_URL}/api/beta/requests?${params}`, {
        headers: {
          'x-admin-key': adminKey,
        },
      });

      if (response.status === 401 || response.status === 403) {
        setIsAuthenticated(false);
        setAuthError('Invalid admin key');
        localStorage.removeItem('adminKey');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch requests');
      }

      const data: RequestsResponse = await response.json();
      setRequests(data.requests);
      setTotalPages(data.totalPages);
      setTotal(data.total);
      setIsAuthenticated(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [adminKey, page, statusFilter]);

  // Fetch requests when authenticated and filters change
  useEffect(() => {
    if (isAuthenticated) {
      fetchRequests();
    }
  }, [isAuthenticated, fetchRequests]);

  /**
   * Handle admin key submission
   */
  function handleAdminKeySubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!adminKey.trim()) {
      setAuthError('Admin key is required');
      return;
    }

    localStorage.setItem('adminKey', adminKey);
    setIsAuthenticated(true);
    setAuthError('');
    fetchRequests();
  }

  /**
   * Approve a beta request
   */
  async function handleApprove(requestId: string) {
    setActionLoading(requestId);

    try {
      const response = await fetch(`${API_BASE_URL}/api/beta/approve/${requestId}`, {
        method: 'POST',
        headers: {
          'x-admin-key': adminKey,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to approve request');
      }

      // Refresh list
      await fetchRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setActionLoading(null);
    }
  }

  /**
   * Reject a beta request
   */
  async function handleReject(requestId: string) {
    setActionLoading(requestId);

    try {
      const response = await fetch(`${API_BASE_URL}/api/beta/reject/${requestId}`, {
        method: 'POST',
        headers: {
          'x-admin-key': adminKey,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to reject request');
      }

      // Refresh list
      await fetchRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setActionLoading(null);
    }
  }

  /**
   * Logout - clear admin key
   */
  function handleLogout() {
    localStorage.removeItem('adminKey');
    setAdminKey('');
    setIsAuthenticated(false);
    setRequests([]);
  }

  /**
   * Format date for display
   */
  function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString();
  }

  // Admin key login form
  if (!isAuthenticated) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.loginCard}>
          <h1 className={styles.title}>Admin Access</h1>
          <p className={styles.subtitle}>Enter your admin key to manage alpha requests</p>

          <form onSubmit={handleAdminKeySubmit} className={styles.loginForm}>
            {authError && (
              <div className={styles.errorMessage}>{authError}</div>
            )}

            <div className={styles.formGroup}>
              <label htmlFor="adminKey" className={styles.label}>
                Admin Key
              </label>
              <input
                id="adminKey"
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                placeholder="Enter admin key"
                className={styles.input}
              />
            </div>

            <button type="submit" className={styles.submitButton}>
              Access Dashboard
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Main dashboard
  return (
    <div className={styles.pageContainer}>
      <div className={styles.dashboard}>
        {/* Header */}
        <header className={styles.header}>
          <h1 className={styles.title}>Alpha Requests</h1>
          <button onClick={handleLogout} className={styles.logoutButton}>
            Logout
          </button>
        </header>

        {/* Stats and Filters */}
        <div className={styles.controls}>
          <div className={styles.stats}>
            <span className={styles.statItem}>Total: {total}</span>
          </div>

          <div className={styles.filters}>
            <label htmlFor="status-filter" className={styles.filterLabel}>
              Status:
            </label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as StatusFilter);
                setPage(1);
              }}
              className={styles.filterSelect}
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className={styles.errorMessage}>{error}</div>
        )}

        {/* Loading state */}
        {loading && (
          <div className={styles.loadingState}>Loading...</div>
        )}

        {/* Requests list */}
        {!loading && requests.length === 0 && (
          <div className={styles.emptyState}>
            No {statusFilter === 'all' ? '' : statusFilter} requests found.
          </div>
        )}

        {!loading && requests.length > 0 && (
          <div className={styles.requestsList}>
            {requests.map((request) => (
              <div key={request.id} className={styles.requestCard}>
                <div className={styles.requestHeader}>
                  <span className={styles.requestEmail}>{request.email}</span>
                  <span className={`${styles.statusBadge} ${styles[request.status]}`}>
                    {request.status}
                  </span>
                </div>

                <div className={styles.requestMeta}>
                  <span>Requested: {formatDate(request.requestedAt)}</span>
                  <span>Source: {request.source}</span>
                  {request.discordHandle && (
                    <span>Discord: {request.discordHandle}</span>
                  )}
                </div>

                <div className={styles.requestMotivation}>
                  <strong>Motivation:</strong>
                  <p>{request.motivation}</p>
                </div>

                {request.status === 'pending' && (
                  <div className={styles.requestActions}>
                    <button
                      onClick={() => handleApprove(request.id)}
                      disabled={actionLoading === request.id}
                      className={styles.approveButton}
                    >
                      {actionLoading === request.id ? 'Processing...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => handleReject(request.id)}
                      disabled={actionLoading === request.id}
                      className={styles.rejectButton}
                    >
                      {actionLoading === request.id ? 'Processing...' : 'Reject'}
                    </button>
                  </div>
                )}

                {request.reviewedAt && (
                  <div className={styles.reviewedInfo}>
                    Reviewed: {formatDate(request.reviewedAt)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className={styles.pageButton}
            >
              Previous
            </button>
            <span className={styles.pageInfo}>
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              className={styles.pageButton}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
